import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const TRANSLATIONS = {
  en: {
    "brand.name": "NxtTechNews",
    "brand.short": "NEXT",
    "brand.intelligence": "TECH NEWS",
    "brand.tagline": "AI-Powered Technology Intelligence",
    "hero.support": "Explore technology information organized with the assistance of AI. Discover relevant developments, understand key points through permitted AI-assisted summaries, and visit original publishers for complete articles.",
    "common.storyFrom": "Story from",
    "common.discovered": "Discovered",
    "common.publishedPending": "Publication date shown once source metadata use is verified",
    "common.limitedDisplay": "Content on NxtTechNews is reviewed on an ongoing basis under our source eligibility standards, to ensure accuracy and respect for publishers' rights. For the complete story, visit the original publisher.",
    "common.readers": "Readers",
    "common.notAuthored": "NxtTechNews did not author this article and does not independently verify third-party reports. Original publishers retain their rights.",
    "beta.badge": "BETA",
    "beta.notice": "NxtTechNews Beta is an evolving AI-powered technology discovery platform. We are continuously improving content discovery, source verification, and user experience.",
    "how.title": "How It Works",
    "how.1.title": "Discover",
    "how.1.desc": "AI-assisted systems identify relevant technology developments through eligible sources.",
    "how.2.title": "Understand",
    "how.2.desc": "The platform organizes information and provides concise AI-assisted summaries when permitted.",
    "how.3.title": "Explore",
    "how.3.desc": "Users can visit the original publisher to read the complete article and consult the original source.",
    "footer.howItWorks": "How It Works",
    "footer.terms": "Terms of Use",
    "consent.text": "We use cookies for analytics (Google Analytics) to understand how readers use NxtTechNews. No analytics cookies are set unless you accept.",
    "consent.accept": "Accept",
    "consent.reject": "Reject",
    "consent.settings": "Cookie settings",
    "report.button": "Report an issue",
    "report.title": "Report an issue with this item",
    "report.type": "Issue type",
    "report.inaccurate_summary": "Inaccurate summary",
    "report.incorrect_attribution": "Incorrect attribution",
    "report.broken_link": "Broken link",
    "report.rights_issue": "Suspected content-rights issue",
    "report.other": "Other",
    "report.message": "Describe the issue",
    "report.email": "Email (optional, only to follow up)",
    "report.submit": "Submit report",
    "report.sent": "Thank you — your report has been received and will be reviewed.",
    "report.cancel": "Cancel",
    "advertise.kicker": "Advertise with NxtTechNews",
    "advertise.title": "Reach decision-makers following AI and emerging technology.",
    "advertise.desc": "NxtTechNews helps technology professionals, founders and researchers discover what matters in AI. Place your brand alongside the developments they follow — with clear sponsorship labelling.",
    "advertise.unique30": "Unique visitors · last 30 days",
    "advertise.pv30": "Page views · last 30 days",
    "advertise.avgDaily": "Average daily visitors",
    "advertise.ppv": "Pages per visit",
    "advertise.trend": "Audience trend · 30 days",
    "advertise.today": "Visitors today",
    "advertise.method": "First-party, privacy-friendly measurement: anonymous visitor IDs, no personal data, admin pages excluded.",
    "advertise.since": "Measured since",
    "advertise.items": "Technology items discovered",
    "advertise.sources": "Registered sources",
    "advertise.formats": "Advertising formats",
    "advertise.banner.title": "Homepage banner",
    "advertise.banner.desc": "A premium full-width placement in the middle of the homepage, seen by every visitor scrolling through the latest discoveries.",
    "advertise.sponsored.title": "Sponsored content card",
    "advertise.sponsored.desc": "A native card woven into the discovery feed in Arabic and English, clearly labelled as sponsored and linking directly to your page.",
    "advertise.ctaTitle": "Let's build your campaign",
    "advertise.ctaDesc": "Get pricing, availability and the full media kit.",
    "advertise.viewKit": "View audience & media kit",
    "sponsored.learnMore": "Learn more",
    "footer.advertise": "Advertise",
    "xp.title": "X Pulse — AI & Tech",
    "xp.desc": "Newsworthy statements posted directly on X by approved technology leaders, AI researchers and official company accounts. AI selects and summarizes; every card links to the original post.",
    "xp.latest": "Latest Posts",
    "xp.tech_leaders": "Tech Leaders",
    "xp.official_company": "Official Company Updates",
    "xp.ai_research": "AI Research",
    "xp.ai_products": "AI Products & Announcements",
    "xp.direct": "Direct Statement · X Post",
    "xp.aiNote": "AI-generated headline & summary of the author's post",
    "xp.unverified": "Statement not independently verified",
    "xp.related": "Related publisher coverage",
    "xp.viewPost": "View original post on X →",
    "xp.allAccounts": "All accounts",
    "xp.search": "Search statements…",
    "xp.from": "From",
    "xp.to": "To",
    "xp.emptyTitle": "X Pulse is warming up",
    "xp.emptyDesc": "Approved accounts are synced through the official X API. Statements will appear here once the connection is active.",
    "xp.syncInfo": "Checked every {n} minutes via the official X API",
    "xp.notReal": "Updated periodically — not real-time",
    "xp.posted": "Posted",
    "brand.byline": "Operated by Trust Ed Techno",

    "hero.badge": "NxtTechNews Beta · AI-Powered Technology Intelligence",
    "hero.title": "Discover Technology. Understand What Matters.",
    "hero.subtitle": "Discover emerging AI developments, explore technology trends, and access original sources through one intelligent platform.",

    "section.topStories": "Top Stories",
    "section.liveNews": "Live Discovery",
    "section.trending": "Trending Topics",
    "section.latest": "Latest Discoveries",
    "section.byCategory": "News by Category",
    "section.viewAll": "View all",
    "section.liveMonitoring": "Continuously discovering technology developments from registered sources.",
    "section.monitoring": "Discovering from registered sources…",

    "feed.refresh": "Refresh News",
    "feed.refreshing": "Refreshing…",
    "feed.updated": "Feed updated",
    "feed.upToDate": "Feed is already up to date.",
    "feed.failed": "Refresh failed. Showing previously loaded news.",
    "feed.lastUpdated": "Last updated",
    "feed.never": "just now",

    "common.readOriginal": "Read Original Article →",
    "common.readOriginalArticle": "Read Original Article →",
    "common.aiSummary": "AI-assisted summary",
    "common.keyPoints": "Key Points",
    "common.verified": "Source attributed",
    "common.pending": "Pending Review",
    "common.failed": "Failed Verification",
    "common.removed": "Removed",
    "common.loadMore": "Load more",
    "common.relatedStories": "Related Stories",
    "common.alsoReportedBy": "Also reported by",
    "common.source": "Source",
    "common.published": "Published",
    "common.retrieved": "Retrieved",
    "common.updated": "Last updated",
    "common.originalUrl": "Original URL",
    "common.sourceCitation": "Source & Citation",
    "common.backToFeed": "Back to feed",
    "common.stories": "stories",
    "common.by": "By",
    "common.loading": "Loading…",
    "common.notFound": "Article not found.",
    "common.searchPlaceholder": "Search OpenAI, Gemini, agents…",
    "common.noStories": "No stories found in this section yet.",

    "article.translate": "View Arabic summary",
    "article.translating": "Translating…",
    "article.showEnglish": "Show English summary",
    "article.englishNote": "This AI summary is shown in its original language (English).",
    "article.aiDisclaimer": "AI-assisted summary based on permitted source material — not original journalism and not an independent verification of the report. Read the full article at the original publisher.",
    "image.licensed": "Licensed image",
    "image.illustration": "Illustrative image",

    "latest.title": "Latest Discoveries",
    "latest.desc": "Chronological feed of newly discovered technology developments. Filter by category, source or date.",
    "filter.allSources": "All sources",
    "filter.anyTime": "Any time",
    "filter.24h": "Last 24 hours",
    "filter.3d": "Last 3 days",
    "filter.7d": "Last 7 days",
    "search.title": "Search",
    "search.desc": "Results across headlines, summaries, sources and categories.",

    "footer.poweredBy": "AI-Powered Technology Intelligence | Operated by Trust Ed Techno",
    "footer.product": "A product of",
    "footer.aiGenerated": "Content on this site is generated with the assistance of AI technologies (discovery, summarization and translation) and may contain errors. Please consult the original source for full context.",
    "footer.disclosure": "NxtTechNews is an AI-powered technology intelligence and content discovery platform operated by Trust Ed Techno. AI assists with discovery, categorization, summarization and translation where permitted. AI-assisted summaries may contain errors or omissions and are not original reporting or independent verification. Third-party content remains the property of its respective publishers and rights holders, and is displayed only to the extent permitted by each source's recorded permissions.",
    "footer.platform": "Platform",
    "footer.company": "Company",
    "footer.legal": "Legal",
    "footer.about": "About",
    "footer.editorial": "Editorial Policy",
    "footer.copyright": "IP & Content Attribution Policy",
    "footer.aiTransparency": "AI Transparency",
    "footer.privacy": "Privacy Policy",
    "footer.contact": "Contact & Corrections",
    "footer.adminPortal": "Admin Portal",
    "footer.rights": "All source content remains the property of its respective publishers and rights holders.",

    "lang.label": "Language",
    "lang.en": "English",
    "lang.ar": "العربية",
    "breaking.label": "Breaking",
    "notify.breaking": "Just discovered",
    "notify.center": "Live alerts",
    "share.button": "Share this story",
    "share.title": "Share story card",
    "share.desc": "A branded NxtTechNews card with the headline and publisher. Links open the story on NxtTechNews.",
    "share.copy": "Copy link",
    "share.copied": "Link copied",
    "share.download": "Image",
    "share.native": "Share image from your phone",
    "notify.markRead": "Mark all read",
    "notify.desktopOn": "Enable desktop alerts",
    "notify.desktopOff": "Turn off desktop alerts",
    "notify.enabledBody": "Desktop alerts are on. We'll ping you when new technology stories land.",
    "notify.read": "Read",
    "notify.dismiss": "Dismiss",
    "ad.label": "Advertisement",
    "ad.contact": "To advertise, contact us:",
    "sponsored.label": "Sponsored",
    "sponsored.title": "Your Sponsored Story Here",
    "sponsored.desc": "Reach a global audience of AI, technology and research readers. Promote your product, launch or announcement in this placement.",
    "sponsored.cta": "Advertise with us",
    "sponsored.disclaimer": "Sponsored content — clearly labelled and not independent editorial reporting.",

    "cat.latest": "Latest",
    "cat.generative-ai": "Generative AI",
    "cat.ai-models": "AI Models",
    "cat.ai-companies": "AI Companies",
    "cat.ai-research": "AI Research",
    "cat.ai-tools": "AI Tools",
    "cat.ai-agents": "AI Agents",
    "cat.robotics": "Robotics",
    "cat.ai-business": "AI Business",
    "cat.ai-regulation": "AI Regulation",
    "cat.ai-safety": "AI Safety",
    "cat.open-source-ai": "Open Source AI",

    "contact.title": "Contact Us",
    "contact.name": "Your name",
    "contact.email": "Your email",
    "contact.message": "Your message",
    "contact.send": "Send Message",
    "contact.sent": "Thank you — your message has been received.",
    "contact.copyrightTitle": "Copyright & Licensing Inquiry",
    "contact.rightsHolder": "Rights holder / publisher",
    "contact.articleUrl": "Article URL (on this site)",
    "contact.submit": "Submit Inquiry",
    "contact.copyrightSent": "Your copyright/licensing inquiry has been received and will be reviewed.",
  },
  ar: {
    "brand.name": "NxtTechNews",
    "brand.short": "نكست",
    "brand.intelligence": "تك نيوز",
    "brand.tagline": "منصّة معلومات تقنية مدعومة بالذكاء الاصطناعي",
    "hero.support": "استكشف معلومات تقنية منظّمة بمساعدة الذكاء الاصطناعي. اكتشف التطورات المهمة، وافهم النقاط الرئيسية عبر ملخصات مسموح بها، وزُر الناشرين الأصليين لقراءة المقالات كاملة.",
    "common.storyFrom": "خبر من",
    "common.discovered": "تاريخ الاكتشاف",
    "common.publishedPending": "يُعرض تاريخ النشر بعد التحقق من صلاحية استخدام بيانات المصدر",
    "common.limitedDisplay": "يخضع المحتوى على NxtTechNews لمراجعة مستمرة وفق معايير أهلية المصادر لدينا، حرصًا على الدقة واحترام حقوق الناشرين. للاطلاع على التفاصيل الكاملة، تفضّل بزيارة الناشر الأصلي.",
    "common.readers": "عدد القراء",
    "common.notAuthored": "لم تكتب NxtTechNews هذا المقال، ولا تتحقق بشكل مستقل مما تنشره الجهات الأخرى. ويحتفظ الناشرون الأصليون بكامل حقوقهم.",
    "beta.badge": "تجريبي",
    "beta.notice": "NxtTechNews (نسخة تجريبية) منصّة متطورة لاكتشاف التقنية مدعومة بالذكاء الاصطناعي. نعمل باستمرار على تحسين اكتشاف المحتوى والتحقق من المصادر وتجربة المستخدم.",
    "how.title": "كيف تعمل المنصّة",
    "how.1.title": "اكتشف",
    "how.1.desc": "تحدد أنظمة مدعومة بالذكاء الاصطناعي التطورات التقنية المهمة عبر مصادر مؤهلة.",
    "how.2.title": "افهم",
    "how.2.desc": "تنظّم المنصّة المعلومات وتقدّم ملخصات موجزة بمساعدة الذكاء الاصطناعي عندما يكون ذلك مسموحًا.",
    "how.3.title": "استكشف",
    "how.3.desc": "يمكنك زيارة الناشر الأصلي لقراءة المقال كاملًا والرجوع إلى المصدر الأصلي.",
    "footer.howItWorks": "كيف تعمل المنصّة",
    "footer.terms": "شروط الاستخدام",
    "consent.text": "نستخدم ملفات تعريف الارتباط (الكوكيز) لأغراض التحليل عبر Google Analytics لفهم كيفية استخدام القراء لـ NxtTechNews. لن تُحفظ أي كوكيز تحليلية ما لم توافق.",
    "consent.accept": "قبول",
    "consent.reject": "رفض",
    "consent.settings": "إعدادات الكوكيز",
    "report.button": "الإبلاغ عن مشكلة",
    "report.title": "الإبلاغ عن مشكلة في هذا العنصر",
    "report.type": "نوع المشكلة",
    "report.inaccurate_summary": "ملخص غير دقيق",
    "report.incorrect_attribution": "نسب غير صحيح",
    "report.broken_link": "رابط معطّل",
    "report.rights_issue": "مشكلة محتملة في حقوق المحتوى",
    "report.other": "أخرى",
    "report.message": "صف المشكلة",
    "report.email": "البريد الإلكتروني (اختياري، للمتابعة فقط)",
    "report.submit": "إرسال البلاغ",
    "report.sent": "شكرًا لك — تم استلام بلاغك وستتم مراجعته.",
    "report.cancel": "إلغاء",
    "advertise.kicker": "أعلن مع NxtTechNews",
    "advertise.title": "صِل إلى صنّاع القرار المهتمين بالذكاء الاصطناعي والتقنيات الناشئة.",
    "advertise.desc": "تساعد NxtTechNews المختصين في التقنية ورواد الأعمال والباحثين على اكتشاف ما يهم في الذكاء الاصطناعي. ضع علامتك التجارية بجانب التطورات التي يتابعونها — مع وسم واضح للمحتوى المموّل.",
    "advertise.unique30": "زوار فريدون · آخر 30 يومًا",
    "advertise.pv30": "مشاهدات الصفحات · آخر 30 يومًا",
    "advertise.avgDaily": "متوسط الزوار اليومي",
    "advertise.ppv": "صفحات لكل زيارة",
    "advertise.trend": "اتجاه الجمهور · 30 يومًا",
    "advertise.today": "زوار اليوم",
    "advertise.method": "قياس مباشر يحترم الخصوصية: معرّفات زوار مجهولة، دون بيانات شخصية، مع استبعاد صفحات الإدارة.",
    "advertise.since": "بدأ القياس في",
    "advertise.items": "العناصر التقنية المكتشفة",
    "advertise.sources": "المصادر المسجّلة",
    "advertise.formats": "أشكال الإعلان",
    "advertise.banner.title": "بانر الصفحة الرئيسية",
    "advertise.banner.desc": "مساحة مميزة بعرض كامل في منتصف الصفحة الرئيسية، يراها كل زائر أثناء تصفّح أحدث الاكتشافات.",
    "advertise.sponsored.title": "بطاقة محتوى مموّل",
    "advertise.sponsored.desc": "بطاقة مدمجة بشكل طبيعي في تدفق الاكتشافات بالعربية والإنجليزية، موسومة بوضوح كمحتوى مموّل وتنقل مباشرة إلى صفحتك.",
    "advertise.ctaTitle": "لنصمّم حملتك معًا",
    "advertise.ctaDesc": "احصل على الأسعار والمساحات المتاحة وملف الوسائط الكامل.",
    "advertise.viewKit": "اطّلع على الجمهور وملف الوسائط",
    "sponsored.learnMore": "اعرف المزيد",
    "footer.advertise": "أعلن معنا",
    "xp.title": "نبض X — الذكاء الاصطناعي والتقنية",
    "xp.desc": "تصريحات مهمة ينشرها مباشرةً على X قادة التقنية وباحثو الذكاء الاصطناعي والحسابات الرسمية للشركات المعتمدة. يختارها الذكاء الاصطناعي ويلخّصها، وكل بطاقة ترتبط بالمنشور الأصلي.",
    "xp.latest": "أحدث المنشورات",
    "xp.tech_leaders": "قادة التقنية",
    "xp.official_company": "تحديثات الشركات الرسمية",
    "xp.ai_research": "أبحاث الذكاء الاصطناعي",
    "xp.ai_products": "منتجات وإعلانات الذكاء الاصطناعي",
    "xp.direct": "تصريح مباشر · منشور على X",
    "xp.aiNote": "العنوان والملخص مولّدان بالذكاء الاصطناعي من منشور صاحب الحساب",
    "xp.unverified": "تصريح لم يُتحقَّق منه بشكل مستقل",
    "xp.related": "تغطية ذات صلة من الناشرين",
    "xp.viewPost": "عرض المنشور الأصلي على X ←",
    "xp.allAccounts": "كل الحسابات",
    "xp.search": "ابحث في التصريحات…",
    "xp.from": "من",
    "xp.to": "إلى",
    "xp.emptyTitle": "نبض X قيد التهيئة",
    "xp.emptyDesc": "تتم مزامنة الحسابات المعتمدة عبر واجهة X الرسمية، وستظهر التصريحات هنا فور تفعيل الاتصال.",
    "xp.syncInfo": "يتم الفحص كل {n} دقيقة عبر واجهة X الرسمية",
    "xp.notReal": "تحديث دوري — ليس لحظيًا",
    "xp.posted": "نُشر",
    "brand.byline": "تُشغّلها Trust Ed Techno",

    "hero.badge": "NxtTechNews (نسخة تجريبية) · منصّة معلومات تقنية مدعومة بالذكاء الاصطناعي",
    "hero.title": "اكتشف التقنية. افهم ما يهم.",
    "hero.subtitle": "اكتشف أحدث تطورات الذكاء الاصطناعي، واستكشف اتجاهات التقنية، وانتقل إلى المصادر الأصلية عبر منصّة ذكية واحدة.",

    "section.topStories": "أبرز الأخبار",
    "section.liveNews": "الاكتشاف المباشر",
    "section.trending": "المواضيع الرائجة",
    "section.latest": "أحدث الاكتشافات",
    "section.byCategory": "الأخبار حسب الفئة",
    "section.viewAll": "عرض الكل",
    "section.liveMonitoring": "نكتشف باستمرار التطورات التقنية من المصادر المسجّلة.",
    "section.monitoring": "جارٍ الاكتشاف من المصادر المسجّلة…",

    "feed.refresh": "تحديث الأخبار",
    "feed.refreshing": "جارٍ التحديث…",
    "feed.updated": "تم تحديث الأخبار",
    "feed.upToDate": "الأخبار محدّثة بالفعل.",
    "feed.failed": "فشل التحديث. يتم عرض الأخبار المحمّلة سابقًا.",
    "feed.lastUpdated": "آخر تحديث",
    "feed.never": "الآن",

    "common.readOriginal": "اقرأ الخبر من المصدر الأصلي ←",
    "common.readOriginalArticle": "اقرأ الخبر من المصدر الأصلي ←",
    "common.aiSummary": "ملخص بمساعدة الذكاء الاصطناعي",
    "common.keyPoints": "النقاط الرئيسية",
    "common.verified": "منسوب إلى مصدره",
    "common.pending": "قيد المراجعة",
    "common.failed": "فشل التحقق",
    "common.removed": "محذوف",
    "common.loadMore": "تحميل المزيد",
    "common.relatedStories": "أخبار ذات صلة",
    "common.alsoReportedBy": "نشره أيضًا",
    "common.source": "المصدر",
    "common.published": "تاريخ النشر",
    "common.retrieved": "تاريخ الجلب",
    "common.updated": "آخر تحديث",
    "common.originalUrl": "الرابط الأصلي",
    "common.sourceCitation": "المصدر والاستشهاد",
    "common.backToFeed": "العودة إلى الأخبار",
    "common.stories": "أخبار",
    "common.by": "بقلم",
    "common.loading": "جارٍ التحميل…",
    "common.notFound": "لم يتم العثور على المقال.",
    "common.searchPlaceholder": "ابحث عن OpenAI، Gemini، الوكلاء…",
    "common.noStories": "لا توجد أخبار في هذا القسم بعد.",

    "article.translate": "عرض الملخص بالعربية",
    "article.translating": "جارٍ الترجمة…",
    "article.showEnglish": "عرض الملخص بالإنجليزية",
    "article.englishNote": "يُعرض هذا الملخص بلغته الأصلية (الإنجليزية).",
    "article.aiDisclaimer": "ملخص بمساعدة الذكاء الاصطناعي من مواد مصدر مسموح بها — ليس عملًا صحفيًا أصليًا ولا تحققًا مستقلًا من الخبر. اقرأ المقال كاملًا لدى الناشر الأصلي.",
    "image.licensed": "صورة مرخّصة",
    "image.illustration": "صورة توضيحية",

    "latest.title": "أحدث الاكتشافات",
    "latest.desc": "عرض زمني لأحدث التطورات التقنية المكتشفة، مع إمكانية التصفية حسب الفئة أو المصدر أو التاريخ.",
    "filter.allSources": "كل المصادر",
    "filter.anyTime": "أي وقت",
    "filter.24h": "آخر 24 ساعة",
    "filter.3d": "آخر 3 أيام",
    "filter.7d": "آخر 7 أيام",
    "search.title": "بحث",
    "search.desc": "نتائج عبر العناوين والملخصات والمصادر والفئات.",

    "footer.poweredBy": "منصّة معلومات تقنية مدعومة بالذكاء الاصطناعي | تُشغّلها Trust Ed Techno",
    "footer.product": "أحد منتجات",
    "footer.aiGenerated": "يتم إنشاء المحتوى على هذا الموقع بمساعدة تقنيات الذكاء الاصطناعي (الاكتشاف والتلخيص والترجمة) وقد يحتوي على أخطاء. يُرجى الرجوع إلى المصدر الأصلي للسياق الكامل.",
    "footer.disclosure": "NxtTechNews منصّة ذكاء تقني واكتشاف محتوى مدعومة بالذكاء الاصطناعي تُشغّلها Trust Ed Techno. يساعد الذكاء الاصطناعي في الاكتشاف والتصنيف والتلخيص والترجمة عندما يكون ذلك مسموحًا. قد تحتوي الملخصات على أخطاء أو نواقص، وهي ليست تقارير أصلية ولا تحققًا مستقلًا. يبقى محتوى الأطراف الثالثة ملكًا لناشريه وأصحاب حقوقه، ولا يُعرض إلا بالقدر الذي تسمح به الصلاحيات المسجّلة لكل مصدر.",
    "footer.platform": "المنصّة",
    "footer.company": "الشركة",
    "footer.legal": "قانوني",
    "footer.about": "من نحن",
    "footer.editorial": "السياسة التحريرية",
    "footer.copyright": "سياسة الملكية الفكرية ونسب المحتوى",
    "footer.aiTransparency": "شفافية الذكاء الاصطناعي",
    "footer.privacy": "سياسة الخصوصية",
    "footer.contact": "التواصل والتصحيحات",
    "footer.adminPortal": "بوابة الإدارة",
    "footer.rights": "يبقى كل محتوى المصادر ملكًا لناشريه وأصحاب الحقوق.",

    "lang.label": "اللغة",
    "lang.en": "English",
    "lang.ar": "العربية",
    "breaking.label": "عاجل",
    "notify.breaking": "اكتُشف للتو",
    "notify.center": "التنبيهات المباشرة",
    "share.button": "شارك هذا الخبر",
    "share.title": "مشاركة بطاقة الخبر",
    "share.desc": "بطاقة بهوية NxtTechNews فيها العنوان واسم الناشر، والرابط يفتح الخبر على موقعنا.",
    "share.copy": "نسخ الرابط",
    "share.copied": "تم نسخ الرابط",
    "share.download": "الصورة",
    "share.native": "مشاركة الصورة من هاتفك",
    "notify.markRead": "تعليم الكل كمقروء",
    "notify.desktopOn": "تفعيل تنبيهات سطح المكتب",
    "notify.desktopOff": "إيقاف تنبيهات سطح المكتب",
    "notify.enabledBody": "تم تفعيل التنبيهات، وسنُعلِمك فور وصول أخبار تقنية جديدة.",
    "notify.read": "اقرأ",
    "notify.dismiss": "إغلاق",
    "ad.label": "إعلان",
    "ad.contact": "للإعلان، تواصل معنا:",
    "sponsored.label": "محتوى مموّل",
    "sponsored.title": "قصتك المموّلة هنا",
    "sponsored.desc": "صِل إلى جمهور عالمي من قرّاء التقنية والذكاء الاصطناعي والأبحاث. روّج لمنتجك أو إطلاقك أو إعلانك في هذه المساحة.",
    "sponsored.cta": "أعلن معنا",
    "sponsored.disclaimer": "محتوى مموّل — مُوسوم بوضوح وليس تقريرًا تحريريًا مستقلًا.",

    "cat.latest": "الأحدث",
    "cat.generative-ai": "الذكاء الاصطناعي التوليدي",
    "cat.ai-models": "نماذج الذكاء الاصطناعي",
    "cat.ai-companies": "شركات الذكاء الاصطناعي",
    "cat.ai-research": "أبحاث الذكاء الاصطناعي",
    "cat.ai-tools": "أدوات الذكاء الاصطناعي",
    "cat.ai-agents": "وكلاء الذكاء الاصطناعي",
    "cat.robotics": "الروبوتات",
    "cat.ai-business": "أعمال الذكاء الاصطناعي",
    "cat.ai-regulation": "تنظيم الذكاء الاصطناعي",
    "cat.ai-safety": "سلامة الذكاء الاصطناعي",
    "cat.open-source-ai": "الذكاء الاصطناعي مفتوح المصدر",

    "contact.title": "اتصل بنا",
    "contact.name": "اسمك",
    "contact.email": "بريدك الإلكتروني",
    "contact.message": "رسالتك",
    "contact.send": "إرسال الرسالة",
    "contact.sent": "شكرًا لك — تم استلام رسالتك.",
    "contact.copyrightTitle": "استفسار حقوق النشر والترخيص",
    "contact.rightsHolder": "صاحب الحقوق / الناشر",
    "contact.articleUrl": "رابط المقال (على هذا الموقع)",
    "contact.submit": "إرسال الاستفسار",
    "contact.copyrightSent": "تم استلام استفسارك بشأن حقوق النشر والترخيص، وستتم مراجعته.",
  },
};

const I18nContext = createContext(null);
const LANG_KEY = "ni_lang";

function readLang() {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === "ar" || saved === "en") return saved;
  } catch (e) { /* storage unavailable */ }
  return "en";
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(readLang);

  const applyDir = useCallback((l) => {
    document.documentElement.lang = l;
    document.documentElement.dir = l === "ar" ? "rtl" : "ltr";
  }, []);

  useEffect(() => { applyDir(lang); }, [lang, applyDir]);

  const setLang = useCallback((l) => {
    if (window.gtag) window.gtag("event", "language_switch", { language: l });
    try { localStorage.setItem(LANG_KEY, l); } catch (e) { /* storage unavailable */ }
    setLangState(l);
  }, []);

  const t = useCallback((key) => (TRANSLATIONS[lang] && TRANSLATIONS[lang][key]) || TRANSLATIONS.en[key] || key, [lang]);
  const catName = useCallback((slug) => t(`cat.${slug}`), [t]);
  const dir = lang === "ar" ? "rtl" : "ltr";
  const pick = useCallback((obj, field) => (lang === "ar" && obj?.[`${field}_ar`]) || obj?.[field], [lang]);
  const title = useCallback((obj) => pick(obj, "title") || `${t("common.storyFrom")} ${obj?.source_name || ""}`, [pick, t]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, catName, dir, pick, title, isRTL: lang === "ar" }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);

export const itemDate = (a) => a?.published_at || a?.discovered_at;

export function formatRelative(iso, lang) {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const min = Math.floor(diff / 60000);
  const ar = lang === "ar";
  if (min < 1) return ar ? "الآن" : "just now";
  if (min < 60) return ar ? `قبل ${min} دقيقة` : `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return ar ? `قبل ${hr} ساعة` : `${hr} hour${hr > 1 ? "s" : ""} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return ar ? `قبل ${day} يوم` : `${day} day${day > 1 ? "s" : ""} ago`;
  return new Date(iso).toLocaleDateString(ar ? "ar" : undefined, { month: "short", day: "numeric" });
}

export function formatExact(iso, lang) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(lang === "ar" ? "ar" : undefined, {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short",
  });
}
